import "./UsuarioFormModal.css";
import { BaseSmallModalFormComponent } from "../../../shared/components/modal/BaseSmallModalFormComponent";
import { UsuarioPlataformaResponse } from "../../../shared/response/seguridad/UsuarioPlataformaResponse";
import { UsuarioPlataformaPayload } from "../../../shared/services/api/payload/UsuarioPlataformaPayload";
import { UsuarioPlataformaSaveService } from "../../services/seguridad/UsuarioPlataformaSaveService";
import { UsuarioPlataformaDeleteService } from "../../services/seguridad/UsuarioPlataformaDeleteService";
import { errorMessageComponent } from "../../../shared/components/error/ErrorMessageComponent";

// Misma regla que Services.Implementations.Seguridad.PasswordReset.PasswordPolicy (el servidor es la
// autoridad; esto solo evita un viaje al servidor para un error obvio).
const PASSWORD_MIN = 8;
const PASSWORD_MAX = 100;
const PASSWORD_HAS_LETTER_RE = /[a-zA-Z]/;
const PASSWORD_HAS_DIGIT_RE = /[0-9]/;

type RoleFlag =
  | "hasSuperAdmin"
  | "hasComercial"
  | "hasPilotos"
  | "hasConductores"
  | "hasHardware"
  | "hasOficina";

type RoleId = "admin" | "comercial" | "flota" | "oficina" | "soporte";

// Un usuario tiene un único rol. Cada rol activa uno o más flags booleanos del API.
// "flota" agrupa pilotos y conductores (mismos permisos → mismos flags).
const ROLE_CARDS: { id: RoleId; label: string; desc: string; flags: RoleFlag[] }[] = [
  { id: "admin",     label: "Admin",              desc: "Acceso total",            flags: ["hasSuperAdmin"] },
  { id: "comercial", label: "Comercial",          desc: "Clientes y cotizaciones", flags: ["hasComercial"] },
  { id: "flota",     label: "Piloto / Conductor", desc: "Flota y vuelos",          flags: ["hasPilotos", "hasConductores"] },
  { id: "oficina",   label: "Oficina",            desc: "Gestión de oficina",      flags: ["hasOficina"] },
  { id: "soporte",   label: "Soporte",            desc: "Solo lectura + hardware", flags: ["hasHardware"] },
];

/**
 * Alta y edición de un usuario de Plataforma. En Nitrofert (`_soloRoles`) solo se edita el rol: nombre y correo se
 * ven pero no se editan (los pone el portal de Nitria), y no hay contraseña, estado activo ni baja. El servidor hace
 * cumplir lo mismo.
 */
export class UsuarioFormModal extends BaseSmallModalFormComponent<
  UsuarioPlataformaResponse,
  UsuarioPlataformaPayload,
  UsuarioPlataformaSaveService
> {
  private _nombreInput!:    HTMLInputElement;
  private _emailInput!:     HTMLInputElement;
  /** `null` en Nitrofert: no hay contraseña. */
  private _passwordInput:   HTMLInputElement | null = null;
  /** `null` en Nitrofert: el estado activo no se edita. */
  private _activoToggle:    HTMLInputElement | null = null;
  private _roleCards!:      NodeListOf<HTMLElement>;
  private _nombreErrorSpan!:   HTMLSpanElement;
  private _emailErrorSpan!:    HTMLSpanElement;
  private _passwordErrorSpan: HTMLSpanElement | null = null;

  constructor(
    private readonly _onSaved:   (item: UsuarioPlataformaResponse) => void,
    private readonly _onDeleted?: (id: string) => void,
    private readonly _item?:     UsuarioPlataformaResponse,
    private readonly _soloRoles = false,
  ) {
    super(new UsuarioPlataformaSaveService());
  }

  protected get title(): string {
    if (this._soloRoles) return "Rol del usuario";
    return this._item ? "Editar usuario" : "Nuevo usuario";
  }

  protected override get saveInitiallyDisabled(): boolean { return true; }

  protected renderBody(): string {
    const nombre   = this._esc(this._item?.nombre ?? "");
    const email    = this._esc(this._item?.email  ?? "");

    const activeRole = this._detectActiveRole();
    const roleCardsHtml = ROLE_CARDS.map(({ id, label, desc }) => {
      const selected = id === activeRole ? "selected" : "";
      return `
        <div class="usr-role-card ${selected}" data-role="${id}" tabindex="0" role="radio" aria-checked="${id === activeRole}">
          <div class="usr-role-card-name">${label}</div>
          <div class="usr-role-card-desc">${desc}</div>
        </div>`;
    }).join("");

    const isActivo       = this._item ? this._item.isActivo !== false : true;
    // En Nitrofert nombre y correo los pone el portal de Nitria: se ven, pero no se editan.
    const soloLectura    = this._soloRoles ? "disabled" : "";

    return `
      <div class="usr-form-fields">

        <div class="usr-form-group">
          <label class="usr-form-label" for="usr-f-nombre">Nombre <span class="usr-req">*</span></label>
          <input id="usr-f-nombre" class="sx-input" type="text" value="${nombre}" maxlength="100" placeholder="Carlos Mejía" ${soloLectura}>
          <span class="sx-field-error" id="usr-f-nombre-error"></span>
        </div>

        <div class="usr-form-group">
          <label class="usr-form-label" for="usr-f-email">Correo electrónico <span class="usr-req">*</span></label>
          <input id="usr-f-email" class="sx-input" type="email" value="${email}" maxlength="250" placeholder="correo@dataag.co" ${soloLectura}>
          <span class="sx-field-error" id="usr-f-email-error"></span>
        </div>

        ${this._soloRoles ? "" : `<div class="usr-form-group">
          <label class="usr-form-label" for="usr-f-pwd">
            Contraseña ${this._item ? "(dejar vacío para no cambiar)" : "(opcional)"}
          </label>
          <input id="usr-f-pwd" class="sx-input" type="password" autocomplete="new-password" placeholder="••••••••" maxlength="${PASSWORD_MAX}">
          <span class="usr-form-hint">
            ${this._item
              ? "Si la llenás, se envían las credenciales nuevas por correo y se cierran las sesiones abiertas de esta cuenta."
              : "Si la dejás vacía, se genera una automática y se envía por correo."}
            Mínimo ${PASSWORD_MIN} caracteres, con al menos una letra y un número.
          </span>
          <span class="sx-field-error" id="usr-f-pwd-error"></span>
        </div>`}

        <div class="usr-form-section">Rol</div>
        <div class="usr-role-grid">${roleCardsHtml}</div>

        ${this._soloRoles ? "" : `<div class="usr-toggle-row">
          <div>
            <div class="usr-toggle-label">Usuario activo</div>
            <div class="usr-toggle-sub">Puede iniciar sesión</div>
          </div>
          <label class="usr-switch" aria-label="Usuario activo">
            <input type="checkbox" id="usr-tgl-activo" ${isActivo ? "checked" : ""}>
            <span class="usr-switch-track"></span>
          </label>
        </div>`}

      </div>
    `;
  }

  protected override onAfterRender(el: HTMLDivElement): void {
    this._nombreInput    = el.querySelector<HTMLInputElement>("#usr-f-nombre")!;
    this._emailInput     = el.querySelector<HTMLInputElement>("#usr-f-email")!;
    this._passwordInput  = el.querySelector<HTMLInputElement>("#usr-f-pwd");
    this._activoToggle   = el.querySelector<HTMLInputElement>("#usr-tgl-activo");
    this._roleCards      = el.querySelectorAll<HTMLElement>(".usr-role-card");
    this._nombreErrorSpan   = el.querySelector<HTMLSpanElement>("#usr-f-nombre-error")!;
    this._emailErrorSpan    = el.querySelector<HTMLSpanElement>("#usr-f-email-error")!;
    this._passwordErrorSpan = el.querySelector<HTMLSpanElement>("#usr-f-pwd-error");

    // role card toggle
    this._roleCards.forEach((card) => {
      card.addEventListener("click", () => this._toggleCard(card));
      card.addEventListener("keydown", (e) => { if ((e as KeyboardEvent).key === " " || (e as KeyboardEvent).key === "Enter") { e.preventDefault(); this._toggleCard(card); } });
    });

    // validation
    this._nombreInput.addEventListener("input", () => this._validate());
    this._emailInput.addEventListener("input", () => this._validate());
    this._passwordInput?.addEventListener("input", () => this._validate());
    this._validate();

    // delete button (edit mode only)
    if (this._item && this._onDeleted) {
      const footer = el.querySelector<HTMLElement>(".sx-dlg-footer")!;
      const delBtn = document.createElement("button");
      delBtn.type = "button";
      delBtn.className = "sx-btn sx-btn-danger sx-btn-sm usr-del-btn";
      delBtn.innerHTML = '<i class="ph ph-trash-simple" aria-hidden="true"></i> Eliminar';
      footer.insertBefore(delBtn, footer.firstChild);
      delBtn.onclick = () => void this._handleDelete(delBtn);
    }
  }

  protected buildPayload(): UsuarioPlataformaPayload {
    const payload = new UsuarioPlataformaPayload();
    payload.id             = this._item?.id ?? null;
    payload.nombre         = this._nombreInput.value.trim();
    payload.email          = this._emailInput.value.trim();
    // En Nitrofert no hay casilla: se manda el estado que tiene (el servidor igual solo guarda los roles).
    payload.isActivo       = this._activoToggle?.checked ?? (this._item?.isActivo !== false);

    // Un único rol → activar solo los flags del rol seleccionado (el resto en false).
    payload.hasSuperAdmin  = false;
    payload.hasComercial   = false;
    payload.hasPilotos     = false;
    payload.hasConductores = false;
    payload.hasHardware    = false;
    payload.hasOficina     = false;
    const selected = ROLE_CARDS.find((r) => r.id === this._selectedRoleId());
    if (selected) selected.flags.forEach((flag) => { payload[flag] = true; });

    const pwd = this._passwordInput?.value;
    if (pwd) payload.password = pwd;
    return payload;
  }

  protected override onSaved(item: UsuarioPlataformaResponse): void {
    this._onSaved(item);
    this.dispose();
  }

  // ── Private helpers ───────────────────────────────────────────────────────────

  private _toggleCard(card: HTMLElement): void {
    // Selección única: al activar un card se deselecciona el resto.
    // Click sobre el card ya activo lo deselecciona (usuario sin rol).
    const willSelect = !card.classList.contains("selected");
    this._roleCards.forEach((c) => {
      const on = c === card && willSelect;
      c.classList.toggle("selected", on);
      c.setAttribute("aria-checked", String(on));
    });
  }

  /** Rol activo del usuario en edición, derivado de sus flags. */
  private _detectActiveRole(): RoleId | null {
    if (!this._item) return null;
    const found = ROLE_CARDS.find((r) => r.flags.some((flag) => this._item?.[flag]));
    return found?.id ?? null;
  }

  private _selectedRoleId(): RoleId | null {
    const card = Array.from(this._roleCards).find((c) => c.classList.contains("selected"));
    return (card?.dataset.role as RoleId | undefined) ?? null;
  }

  private _validate(): void {
    const nombreError = this._nombreInput.value.trim().length >= 1 ? null : "Ingrese el nombre";
    const emailError = this._getEmailError(this._emailInput.value.trim());
    const passwordError = this._getPasswordError(this._passwordInput?.value ?? "");

    this._setFieldError(this._nombreInput, this._nombreErrorSpan, nombreError);
    this._setFieldError(this._emailInput, this._emailErrorSpan, emailError);
    if (this._passwordInput && this._passwordErrorSpan) {
      this._setFieldError(this._passwordInput, this._passwordErrorSpan, passwordError);
    }

    this.setSaveDisabled(!!(nombreError || emailError || passwordError));
  }

  private _getEmailError(email: string): string | null {
    if (email.length < 3 || !email.includes("@")) return "Ingrese un email válido";
    return null;
  }

  /** Vacía es válida (campo opcional); llena, debe cumplir la misma regla que el servidor. */
  private _getPasswordError(password: string): string | null {
    if (!password) return null;
    if (password.length < PASSWORD_MIN || password.length > PASSWORD_MAX) {
      return `Debe tener entre ${PASSWORD_MIN} y ${PASSWORD_MAX} caracteres`;
    }
    if (!PASSWORD_HAS_LETTER_RE.test(password) || !PASSWORD_HAS_DIGIT_RE.test(password)) {
      return "Debe incluir al menos una letra y un número";
    }
    return null;
  }

  private _setFieldError(input: HTMLInputElement, span: HTMLSpanElement, message: string | null): void {
    span.textContent = message ?? "";
    input.classList.toggle("error", message !== null);
  }

  private async _handleDelete(btn: HTMLButtonElement): Promise<void> {
    if (!this._item || !this._onDeleted) return;
    if (!window.confirm(`¿Eliminar al usuario ${this._item.nombre}? Esta acción no se puede deshacer.`)) return;

    btn.disabled = true;
    btn.textContent = "Eliminando…";
    try {
      await new UsuarioPlataformaDeleteService().delete(this._item.id);
      this._onDeleted(this._item.id);
      this.dispose();
    } catch (error) {
      errorMessageComponent.addError("Error al eliminar usuario", (error as Error).message);
      btn.disabled = false;
      btn.innerHTML = '<i class="ph ph-trash-simple" aria-hidden="true"></i> Eliminar';
    }
  }

  private _esc(s: string): string {
    return (s ?? "").replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
  }
}
